
import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { PlusCircle, Edit, Trash2, FileText, ArrowUpDown } from 'lucide-react';
import BusinessProgressService, { BusinessProgress } from '@/services/BusinessProgressService';
import BusinessForm from './BusinessForm';
import { useAuth } from '@/contexts/AuthContext';

const BusinessProgressManager = () => {
  const { user } = useAuth();
  const [businesses, setBusinesses] = useState<BusinessProgress[]>([]);
  const [selectedBusiness, setSelectedBusiness] = useState<BusinessProgress | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
  const [businessToDelete, setBusinessToDelete] = useState<string | null>(null);
  const [sortField, setSortField] = useState<string>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const fetchBusinesses = async () => {
    setIsLoading(true);
    try {
      const data = await BusinessProgressService.getBusinessProgress();
      console.log('Fetched businesses:', data);
      setBusinesses(data);
    } catch (error) {
      console.error('Error fetching businesses:', error);
      toast.error('Failed to load businesses');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBusinesses();
  }, []);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedBusinesses = [...businesses].sort((a, b) => {
    if (sortField === 'business_name') {
      return sortDirection === 'asc'
        ? a.business_name.localeCompare(b.business_name)
        : b.business_name.localeCompare(a.business_name);
    } else if (sortField === 'development_stage') {
      return sortDirection === 'asc'
        ? a.development_stage.localeCompare(b.development_stage)
        : b.development_stage.localeCompare(a.development_stage);
    } else if (sortField === 'created_at') {
      return sortDirection === 'asc'
        ? new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        : new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    }
    return 0;
  });

  const handleAddNew = () => {
    setSelectedBusiness(null);
    setIsFormDialogOpen(true);
  };

  const handleEdit = (business: BusinessProgress) => {
    setSelectedBusiness(business);
    setIsFormDialogOpen(true);
  };

  const handleSave = () => {
    setIsFormDialogOpen(false);
    fetchBusinesses();
  };

  const confirmDelete = (id: string) => {
    setBusinessToDelete(id);
    setIsDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!businessToDelete) return;
    
    try {
      await BusinessProgressService.deleteBusinessProgress(businessToDelete);
      toast.success('Business deleted successfully');
      fetchBusinesses();
    } catch (error) {
      console.error('Error deleting business:', error);
      toast.error('Failed to delete business');
    } finally {
      setIsDeleteDialogOpen(false);
      setBusinessToDelete(null);
    }
  };

  const getStageBadge = (stage: string) => {
    switch (stage) {
      case 'development':
        return <Badge className="bg-amber-100 text-amber-800">Development</Badge>;
      case 'launch':
        return <Badge className="bg-blue-100 text-blue-800">Launch</Badge>;
      case 'growth':
        return <Badge className="bg-green-100 text-green-800">Growth</Badge>;
      default:
        return <Badge>{stage}</Badge>;
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Business Progress</CardTitle>
            <CardDescription>Manage business development progress</CardDescription>
          </div>
          <Button onClick={handleAddNew}>
            <PlusCircle className="mr-2 h-4 w-4" />
            Add New Business
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-10 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-solid border-current border-r-transparent"></div>
              <p className="mt-2 text-sm text-gray-500">Loading businesses...</p>
            </div>
          ) : businesses.length === 0 ? (
            <div className="text-center py-10">
              <p className="text-gray-500">No businesses found</p>
              <Button variant="outline" className="mt-4" onClick={handleAddNew}>
                Add your first business
              </Button>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="cursor-pointer" onClick={() => handleSort('business_name')}>
                      <div className="flex items-center">
                        Business Name
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </div>
                    </TableHead>
                    <TableHead className="cursor-pointer" onClick={() => handleSort('development_stage')}>
                      <div className="flex items-center">
                        Stage
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </div>
                    </TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="cursor-pointer" onClick={() => handleSort('created_at')}>
                      <div className="flex items-center">
                        Created
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </div>
                    </TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedBusinesses.map((business) => (
                    <TableRow key={business.id}>
                      <TableCell className="font-medium">{business.business_name}</TableCell>
                      <TableCell>{getStageBadge(business.development_stage)}</TableCell>
                      <TableCell className="max-w-xs truncate">{business.description}</TableCell>
                      <TableCell>{new Date(business.created_at).toLocaleDateString()}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end space-x-2">
                          <Button variant="outline" size="sm" onClick={() => handleEdit(business)}>
                            <Edit className="h-4 w-4" />
                            <span className="sr-only">Edit</span>
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => confirmDelete(business.id)}>
                            <Trash2 className="h-4 w-4" />
                            <span className="sr-only">Delete</span>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Form Dialog */}
      <Dialog open={isFormDialogOpen} onOpenChange={setIsFormDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{selectedBusiness ? 'Edit Business' : 'Add New Business'}</DialogTitle>
            <DialogDescription>
              {selectedBusiness
                ? 'Update the business details below.'
                : 'Enter the details for the new business.'}
            </DialogDescription>
          </DialogHeader>
          <BusinessForm 
            business={selectedBusiness || undefined}
            onSave={handleSave}
            onCancel={() => setIsFormDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this business? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BusinessProgressManager;
